import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DialogTopBar } from '../components/DialogTopBar';
import { toast } from 'sonner';
import { Plus, GraduationCap, Edit, Trash2, Upload, Search, FileDown, Loader2, X, Archive, Download, MoreHorizontal } from 'lucide-react';
import * as XLSX from 'xlsx';
import { PROGRAMME_OPTIONS } from '../lib/poeConstants';

const StudentsPage = () => {
    const { getAuthHeaders, API } = useAuth();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState('all');
    const [selectedEnrollYear, setSelectedEnrollYear] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [globalGroups, setGlobalGroups] = useState([]);
    const [qualOptions, setQualOptions] = useState([]);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        student_id: '',
        year: '1',
        enrollment_year: '',
        id_number: '',
        programme: '',
        qualifications: []
    });

    const currentYear = new Date().getFullYear();
    const enrollmentYearOptions = Array.from({ length: 10 }, (_, i) => currentYear + 1 - i);

    useEffect(() => {
        fetchStudents();
        fetchGlobalGroups();
        fetchQualifications();
    }, []);

    const fetchQualifications = async () => {
        try {
            const res = await axios.get(`${API}/poe/qualifications`, { headers: getAuthHeaders() });
            setQualOptions(res.data.qualifications || []);
        } catch (error) { /* silent */ }
    };

    const fetchStudents = async () => {
        try {
            const response = await axios.get(`${API}/students`, { headers: getAuthHeaders() });
            setStudents(response.data);
        } catch (error) {
            console.error('Failed to fetch students:', error);
            toast.error('Failed to load students');
        } finally {
            setLoading(false);
        }
    };

    const fetchGlobalGroups = async () => {
        try {
            const response = await axios.get(`${API}/global-groups`, { headers: getAuthHeaders() });
            setGlobalGroups(response.data);
        } catch (error) { /* silent */ }
    };

    const getStudentGroup = (studentId) => {
        return globalGroups.find(g => (g.student_ids || []).includes(studentId));
    };

    const openModal = (student = null) => {
        if (student) {
            setEditingStudent(student);
            setFormData({
                name: student.name,
                email: student.email || '',
                student_id: student.student_id,
                year: student.year.toString(),
                enrollment_year: student.enrollment_year ? student.enrollment_year.toString() : '',
                id_number: student.id_number || '',
                programme: student.programme || '',
                qualifications: student.qualifications || []
            });
        } else {
            setEditingStudent(null);
            setFormData({ name: '', email: '', student_id: '', year: '1', enrollment_year: '', id_number: '', programme: '', qualifications: [] });
        }
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.student_id) {
            toast.error('Please fill in required fields');
            return;
        }
        if (!formData.enrollment_year) {
            toast.error('Please select an enrollment year');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...formData,
                year: parseInt(formData.year),
                enrollment_year: parseInt(formData.enrollment_year)
            };

            if (editingStudent) {
                await axios.put(`${API}/students/${editingStudent.id}`, payload, { headers: getAuthHeaders() });
                toast.success('Student updated successfully');
            } else {
                await axios.post(`${API}/students`, payload, { headers: getAuthHeaders() });
                toast.success('Student added successfully');
            }
            
            setModalOpen(false);
            fetchStudents();
        } catch (error) {
            console.error('Failed to save student:', error);
            toast.error(error.response?.data?.detail || 'Failed to save student');
        } finally {
            setSaving(false);
        }
    };

    const handleArchive = async (student) => {
        try {
            await axios.post(`${API}/students/${student.id}/archive`, {}, { headers: getAuthHeaders() });
            fetchStudents();
            toast.success(`${student.name} archived`, {
                action: {
                    label: 'Undo', onClick: async () => {
                        try {
                            await axios.post(`${API}/students/${student.id}/restore`, {}, { headers: getAuthHeaders() });
                            fetchStudents();
                            toast.success('Student restored');
                        } catch { toast.error('Failed to restore'); }
                    }
                }
            });
        } catch (error) {
            console.error('Failed to archive student:', error);
            toast.error(error.response?.data?.detail || 'Failed to archive student');
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(`${API}/students/upload`, formData, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'multipart/form-data'
                }
            });

            const { created, updated, skipped, groups_created, groups_updated, errors } = response.data;
            const parts = [];
            if (created > 0) parts.push(`${created} added`);
            if (updated > 0) parts.push(`${updated} updated`);
            if (skipped > 0) parts.push(`${skipped} skipped`);
            if (groups_created > 0) parts.push(`${groups_created} groups created`);
            if (groups_updated > 0) parts.push(`${groups_updated} groups updated`);
            
            if (parts.length > 0) {
                toast.success(`Upload complete: ${parts.join(', ')}`);
            } else if (errors?.length > 0) {
                toast.error(`Upload failed: ${errors.length} row(s) had errors. ${errors[0]}`);
            } else {
                toast.warning('No students were processed. Check your file format.');
            }
            if (errors?.length > 0 && parts.length > 0) {
                toast.warning(`${errors.length} row(s) had errors: ${errors[0]}`);
            }
            
            setUploadModalOpen(false);
            fetchStudents();
            fetchGlobalGroups();
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error(error.response?.data?.detail || 'Failed to upload file');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const exportStudents = async () => {
        try {
            const yearParam = selectedYear !== 'all' ? `?year=${selectedYear}` : '';
            const response = await axios.get(`${API}/students/export${yearParam}`, {
                headers: getAuthHeaders(),
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', selectedYear !== 'all' ? `students_year${selectedYear}.xlsx` : 'students_all.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Student list exported');
        } catch (error) {
            toast.error('Failed to export students');
        }
    };

    const downloadTemplate = () => {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([
            ['Name', 'Email', 'Student ID', 'Year of Study', 'Enrollment Year', 'ID Number', 'Programme', 'Qualification', 'Group'],
            ['John Smith', 'john@example.com', 'STU001', 1, 2025, '9001011234081', 'ICA Two-year Culinary Arts & Advanced Patisserie', '603/6949/8; 603/6960/7', 'Group A'],
            ['Jane Doe', 'jane@example.com', 'STU002', 2, 2024, '9102025678082', 'ICA Three-year Advanced Culinary Arts, Advanced Patisserie', '603/6960/7', 'Group B']
        ]);
        XLSX.utils.book_append_sheet(wb, ws, 'Students');
        XLSX.writeFile(wb, 'student_template.xlsx');
        toast.success('Template downloaded');
    };

    const handleExportPDF = async (student) => {
        try {
            const response = await axios.get(`${API}/reports/student/${student.id}/pdf`, {
                headers: getAuthHeaders(),
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `student_report_${student.student_id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Report downloaded');
        } catch (error) {
            console.error('Failed to export PDF:', error);
            toast.error('Failed to export report');
        }
    };

    const sortBySurname = (list) => [...list].sort((a, b) => {
        if ((a.year || 0) !== (b.year || 0)) return (a.year || 0) - (b.year || 0);
        const surnameA = (a.name || '').split(' ').pop().toLowerCase();
        const surnameB = (b.name || '').split(' ').pop().toLowerCase();
        return surnameA.localeCompare(surnameB);
    });

    const filteredByYear = (selectedYear === 'all'
        ? students
        : students.filter(s => s.year === parseInt(selectedYear))
    ).filter(s => selectedEnrollYear === 'all' || s.enrollment_year === parseInt(selectedEnrollYear));

    const filteredStudents = sortBySurname(filteredByYear.filter(s => 
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.student_id.toLowerCase().includes(searchQuery.toLowerCase())
    ));

    const yearCounts = {
        1: students.filter(s => s.year === 1).length,
        2: students.filter(s => s.year === 2).length,
        3: students.filter(s => s.year === 3).length
    };

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="students-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">Students</h1>
                        <p className="text-gray-500 mt-1">Manage student records by year</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button 
                            variant="outline"
                            onClick={exportStudents}
                            data-testid="export-students-btn"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Export XLSX
                        </Button>
                        <Button 
                            variant="outline"
                            onClick={() => setUploadModalOpen(true)}
                            data-testid="upload-students-btn"
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            Upload XLSX
                        </Button>
                        <Button 
                            onClick={() => openModal()}
                            data-testid="add-student-btn"
                            
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Student
                        </Button>
                    </div>
                </div>

                {/* Year Tabs */}
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setSelectedYear('all')}
                        data-testid="filter-all"
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            selectedYear === 'all' 
                                ? 'bg-ica-navy text-white' 
                                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                        }`}
                    >
                        All Students ({students.length})
                    </button>
                    {[1, 2, 3].map(year => (
                        <button
                            key={year}
                            onClick={() => setSelectedYear(year.toString())}
                            data-testid={`filter-year-${year}`}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                selectedYear === year.toString() 
                                    ? 'bg-ica-gold text-white' 
                                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                            }`}
                        >
                            Year {year} ({yearCounts[year]})
                        </button>
                    ))}
                </div>

                {/* Search & Enrollment Year Filter */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative max-w-md flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            data-testid="search-students"
                            placeholder="Search by name or ID..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    <Select value={selectedEnrollYear} onValueChange={setSelectedEnrollYear}>
                        <SelectTrigger className="w-full sm:w-56" data-testid="filter-enrollment-year">
                            <SelectValue placeholder="All Enrollment Years" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Enrollment Years</SelectItem>
                            {[...new Set(students.map(s => s.enrollment_year).filter(Boolean))].sort((a, b) => b - a).map(yr => (
                                <SelectItem key={yr} value={yr.toString()}>Enrolled {yr}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Students Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="space-y-3 p-4">
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} className="flex items-center gap-4">
                                    <div className="skeleton h-10 w-10 rounded-full"></div>
                                    <div className="skeleton h-5 flex-1 rounded"></div>
                                    <div className="skeleton h-5 w-24 rounded"></div>
                                    <div className="skeleton h-5 w-16 rounded"></div>
                                </div>
                            ))}
                        </div>
                    ) : filteredStudents.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full ica-table">
                                <thead>
                                    <tr>
                                        <th className="px-6 py-4 text-left">Student</th>
                                        <th className="px-6 py-4 text-left">ID</th>
                                        <th className="px-6 py-4 text-left">Group</th>
                                        <th className="px-6 py-4 text-left">Email</th>
                                        <th className="px-6 py-4 text-center">Year of Study</th>
                                        <th className="px-6 py-4 text-center">Enrollment Year</th>
                                        <th className="px-6 py-4 text-left">Qualification</th>
                                        <th className="px-6 py-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredStudents.map((student) => (
                                        <tr key={student.id} className="border-b border-gray-100 hover:bg-ica-gold/5 transition-colors" data-testid={`student-row-${student.id}`}>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-ica-navy/10 rounded-full flex items-center justify-center">
                                                        <GraduationCap className="w-5 h-5 text-ica-navy" />
                                                    </div>
                                                    <span className="font-medium text-gray-900">{student.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-gray-600 font-mono text-sm">{student.student_id}</td>
                                            <td className="px-6 py-4">
                                                {(() => {
                                                    const grp = getStudentGroup(student.id);
                                                    return grp ? (
                                                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200" data-testid={`student-group-badge-${student.id}`}>
                                                            {grp.name}
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">—</span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-6 py-4 text-gray-500">{student.email || '-'}</td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                                    student.year === 1 ? 'bg-ica-navy/10 text-ica-navy' :
                                                    student.year === 2 ? 'bg-ica-gold/10 text-ica-gold-dark' :
                                                    'bg-ica-steel/20 text-ica-steel-dark'
                                                }`}>
                                                    Year {student.year}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center" data-testid={`student-enrollment-year-${student.id}`}>
                                                {student.enrollment_year ? (
                                                    <span className="text-sm font-medium text-gray-700">{student.enrollment_year}</span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4" data-testid={`student-quals-${student.id}`}>
                                                <div className="flex flex-wrap gap-1">
                                                    {(student.qualifications || []).length > 0 ? (student.qualifications || []).map(c => (
                                                        <span key={c} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-ica-navy/10 text-ica-navy">{c}</span>
                                                    )) : <span className="text-xs text-gray-400">—</span>}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="outline" size="sm" data-testid={`student-actions-${student.id}`} title="Actions">
                                                            <MoreHorizontal className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => handleExportPDF(student)} data-testid={`export-student-${student.id}`}>
                                                            <FileDown className="w-3.5 h-3.5 mr-2" /> Export PDF
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => openModal(student)} data-testid={`edit-student-${student.id}`}>
                                                            <Edit className="w-3.5 h-3.5 mr-2" /> Edit student
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => handleArchive(student)} className="text-red-600 focus:text-red-600" data-testid={`archive-student-${student.id}`}>
                                                            <Archive className="w-3.5 h-3.5 mr-2" /> Archive student
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-10">
                            <GraduationCap className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                            <h3 className="text-base font-semibold text-gray-700 mb-1">No students found</h3>
                            <p className="text-gray-500 mb-4">Add students manually or upload an XLSX file</p>
                            <Button onClick={() => openModal()} >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Student
                            </Button>
                        </div>
                    )}
                </div>

                {/* Add/Edit Modal */}
                <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                    <DialogContent data-testid="student-modal">
                        <DialogTopBar title={<>{editingStudent ? 'Edit Student' : 'Add New Student'}</>}>
    <Button size="sm" variant="outline" onClick={() => setModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button size="sm" 
                                onClick={handleSubmit}
                                disabled={saving}
                                data-testid="save-student-btn"
                                
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    editingStudent ? 'Update Student' : 'Add Student'
                                )}
                            </Button>
</DialogTopBar>
                        
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>Full Name *</Label>
                                <Input
                                    data-testid="student-name-input"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Enter student's full name"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Student ID *</Label>
                                <Input
                                    data-testid="student-id-input"
                                    value={formData.student_id}
                                    onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                                    placeholder="e.g., STU001"
                                    disabled={!!editingStudent}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input
                                    data-testid="student-email-input"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="student@example.com"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Year of Study *</Label>
                                <Select 
                                    value={formData.year} 
                                    onValueChange={(value) => setFormData({ ...formData, year: value })}
                                >
                                    <SelectTrigger data-testid="student-year-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1">Year 1</SelectItem>
                                        <SelectItem value="2">Year 2</SelectItem>
                                        <SelectItem value="3">Year 3</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Enrollment Year *</Label>
                                <Select 
                                    value={formData.enrollment_year} 
                                    onValueChange={(value) => setFormData({ ...formData, enrollment_year: value })}
                                >
                                    <SelectTrigger data-testid="student-enrollment-year-select">
                                        <SelectValue placeholder="Select enrollment year" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {enrollmentYearOptions.map(yr => (
                                            <SelectItem key={yr} value={yr.toString()}>{yr}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>ID Number</Label>
                                <Input
                                    data-testid="student-id-number-input"
                                    value={formData.id_number}
                                    onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                                    placeholder="National ID number (for POE cover)"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Registered ICA Programme</Label>
                                <Select 
                                    value={formData.programme} 
                                    onValueChange={(value) => setFormData({ ...formData, programme: value })}
                                >
                                    <SelectTrigger data-testid="student-programme-select">
                                        <SelectValue placeholder="Select programme" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PROGRAMME_OPTIONS.map(p => (
                                            <SelectItem key={p} value={p}>{p}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Qualifications</Label>
                                <div className="flex flex-wrap gap-2" data-testid="student-qualifications">
                                    {qualOptions.length === 0 && <span className="text-xs text-gray-400">No qualifications configured (set up under POE Setup)</span>}
                                    {qualOptions.map(q => {
                                        const checked = (formData.qualifications || []).includes(q.code);
                                        return (
                                            <button key={q.code} type="button"
                                                onClick={() => setFormData(fd => ({ ...fd, qualifications: checked ? fd.qualifications.filter(c => c !== q.code) : [...(fd.qualifications || []), q.code] }))}
                                                data-testid={`student-qual-toggle-${q.code.replace(/\//g, '-')}`}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${checked ? 'bg-ica-navy text-white border-ica-navy' : 'bg-white text-gray-600 border-gray-300 hover:border-ica-navy'}`}>
                                                {q.title} ({q.code})
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Upload Modal */}
                <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
                    <DialogContent data-testid="upload-modal">
                        <DialogHeader>
                            <DialogTitle>Upload Students</DialogTitle>
                        </DialogHeader>
                        
                        <div className="py-4">
                            <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                <p className="text-gray-600 mb-2">Upload an Excel file (.xlsx)</p>
                                <p className="text-sm text-gray-500 mb-4">
                                    Required columns: Name, Student ID, Year of Study<br />
                                    Optional: Email, Enrollment Year, ID Number, Programme, Group
                                </p>
                                
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    id="file-upload"
                                    data-testid="file-input"
                                />
                                
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    <Button 
                                        variant="outline"
                                        onClick={downloadTemplate}
                                        data-testid="download-template-btn"
                                    >
                                        <FileDown className="w-4 h-4 mr-2" />
                                        Download Template
                                    </Button>
                                    <label htmlFor="file-upload">
                                        <Button 
                                            asChild
                                            disabled={uploading}
                                            className="bg-ica-gold hover:bg-ica-gold-dark cursor-pointer"
                                        >
                                            <span data-testid="select-file-btn">
                                                {uploading ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Uploading...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Upload className="w-4 h-4 mr-2" />
                                                        Select File
                                                    </>
                                                )}
                                            </span>
                                        </Button>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};

export default StudentsPage;
